import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";
import { useApp } from "../App/useApp";

import config from "../../common/config";
import AuthService from "../../services/auth.service";
import { AnalyticsCategory, AnalyticsService } from "../../services/analytics.service";
import { ApplicationEnum } from "../../enums/ApplicationEnum";
import { HomeHealthFrequencyWatchEnableAgenciesEnum } from "../../enums/HomeHealthFrequencyWatchEnableAgenciesEnum";

import Organizations from "../../resources-vue/vue-dialogs/organizations/organizations.vue";
import Alert from "../../resources-vue/vue-dialogs/Alert/Alert.vue";

const organizationId = "organization-id";
const organizationName = "organization-name";

export default defineComponent({
    data() {
        return {
            dialogReference: this.dialogRef as any,
            _authService: null,
            _analyticsService: null as AnalyticsService,
            _logCategory: "No-authorization",
            organizationsList: [],
            selectedOrganization: null,
            dialogRef: null,
            routeName: null
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    created() {
        this._authService = Container.instance.get(AuthService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.routeName = this.$route?.query.name;
    },
    computed: {
        ...mapState(useApp, ["organizations", "isClinician"])
    },

    async mounted() {
        this.organizationsList = this.organizations.map((organization, index) => {
            return {
                name: organization.name,
                id: organization.id,
                message: ""
            };
        });
        await this.switchOrganization();
    },

    methods: {
        ...mapActions(useApp, ["setFrequencyWatch"]),
        cancel() {
            this.dialogReference.close("cancel");
        },
        ok() {
            this.selectedOrganization.message = "success";
            this.dialogReference.close(this.selectedOrganization);
        },

        async switchOrganization() {
            if (this.organizationsList.length <= 0) {
                this.handleNoAccounts();
            } else {
                if (this.organizationsList.length === 1) {
                    window.localStorage.setItem(organizationId, this.organizationsList[0].id.toString());
                    window.localStorage.setItem(organizationName, this.organizationsList[0].name);
                    this.handleFrequencyWatchNavs(this.organizationsList[0].id.toString());
                    this._analyticsService.logEvent({
                        category: AnalyticsCategory.CentralOrganizationSelect,
                        action: `${organizationName} organization selected`
                    });
                    this.$router.push({ name: this.routeName });
                    const profileUrl = `${config.authorityUrl}profile?originName=Axxess Central&originUrl=${window.location.origin.concat("/axxess-central/dashboard")}`;
                    localStorage.setItem("profileUrl", profileUrl);
                } else {
                    await this.handleMultipleAccounts();
                }
            }
        },

        async handleNoAccounts() {
            let model = {
                title: `Authorization Failed`,
                message:
                    "You do not have any agencies linked to your account. If you believe this an error, please contact support."
            };

            this.dialogRef = this.$dialog.open(Alert, {
                props: {
                    modal: true,
                    contentStyle: {
                        width: "400px",
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    model
                },
                onClose: (response) => {
                    this._authService.logout();
                    this._analyticsService.logEvent({
                        category: this._logCategory,
                        action: "Log-Out"
                    });
                }
            });
        },

        async handleMultipleAccounts() {
            this.dialogRef = this.$dialog.open(Organizations, {
                props: {
                    modal: true,
                    contentStyle: {
                        width: "400px",
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                onClose: (response) => {
                    if (response.data.message == "success") {
                        const profileUrl = `${config.authorityUrl}profile?originName=Axxess Central&originUrl=${window.location.origin.concat("/axxess-central/dashboard")}`;
                        localStorage.setItem("profileUrl", profileUrl);
                        window.localStorage.setItem(organizationId, response.data.id);
                        window.localStorage.setItem("reedirectId", response.data.id);
                        window.localStorage.setItem(organizationName, response.data.name);
                        window.localStorage.setItem("reedirectName", response.data.name);
                        this._analyticsService.logEvent({
                            category: AnalyticsCategory.CentralOrganizationSelect,
                            action: `${organizationName} organization selected`
                        });
                        this.handleFrequencyWatchNavs(response.data?.id);
                        this.$router.push({
                            name: this.routeName
                        });
                    } else if (response.data == "cancel") {
                        if (
                            localStorage.getItem(config.organizationId) === null ||
                            this.$route.query.fromPlanner === "true"
                        ) {
                            if (this.isClinician) {
                                this.$router.push("/clinician");
                            } else {
                                this.$router.push("/help");
                            }
                        } else {
                            this.$router.push({ name: this.routeName });
                        }
                    } else {
                        this.$router.push({ name: this.routeName });
                    }
                }
            });
        },
        handleFrequencyWatchNavs(id: string) {
            const currentOrganization = this.organizations.find((organization) => organization.id === id);
            if (!currentOrganization) {
                this.setFrequencyWatch({
                    showFrequencyWatch: false,
                    showHHNav: false,
                    showHosNav: false
                });
                return;
            }
            const orgAgencies = currentOrganization.agencies;
            const showHosNav = orgAgencies.some((agency) => agency.application === ApplicationEnum.AxxessHospiceFE);
            let showHHNav = orgAgencies.some((agency) => agency.application === ApplicationEnum.AgencyCore);
            const hasFrequencyWatchEnabledAgencyHomeHealth = orgAgencies.some((agency) => {
                const identity = agency.id.split("_")[0];
                return (
                    agency.application === ApplicationEnum.AgencyCore &&
                    (identity === HomeHealthFrequencyWatchEnableAgenciesEnum.HomeHealth ||
                        identity === HomeHealthFrequencyWatchEnableAgenciesEnum.EnterpriseSalesDemo)
                );
            });
            if (!hasFrequencyWatchEnabledAgencyHomeHealth) {
                showHHNav = false;
            }
            const showNav = showHHNav || showHosNav;
            this.setFrequencyWatch({
                showFrequencyWatch: showNav,
                showHHNav,
                showHosNav
            });
        }
    }
});
